'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { useChecklist } from '../hooks/use-checklist';
import type { ChecklistTemplate, Checklist } from '../types/checklist.types';
import { useEffect, useState } from 'react';
import { checklistService } from '../services/checklist-service';

type ChecklistContextType = ReturnType<typeof useChecklist>;

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

export function ChecklistProvider({ children }: { children: ReactNode }) {
  const value = useChecklist();
  
  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
}

export function useChecklistContext() {
  const context = useContext(ChecklistContext);
  if (context === undefined) {
    throw new Error('useChecklistContext must be used within a ChecklistProvider');
  }
  return context;
}

// Helper hooks for common patterns
export function useTemplate(templateId: string) {
  const { templates, loading, error } = useChecklistContext();
  const [template, setTemplate] = React.useState<ChecklistTemplate | null>(null);
  
  React.useEffect(() => {
    if (!loading && !error) {
      const found = templates.find(t => t.id === templateId) || null;
      setTemplate(found);
    }
  }, [templateId, templates, loading, error]);
  
  return { template, loading, error };
}



export function useChecklistById(checklistId: string) {
  const { checklists, loading, error } = useChecklistContext();
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function fetchChecklist() {
      setFetching(true);
      setFetchError(null);
      try {
        const fresh = await checklistService.getChecklist(checklistId, checklists[0]?.tenantId || '');
        if (isMounted) setChecklist(fresh);
      } catch (err) {
        if (isMounted) setFetchError(err instanceof Error ? err : new Error('Failed to fetch checklist'));
      } finally {
        if (isMounted) setFetching(false);
      }
    }
    if (checklistId) {
      fetchChecklist();
    } else {
      setChecklist(null);
    }
    return () => {
      isMounted = false;
    };
  }, [checklistId]);

  return { checklist, loading: loading || fetching, error: error || fetchError };
}
